import { useCallback, useEffect, useRef, useState } from "react";
import Checkbox from "../components/Checkbox";
import { store } from "../stores/Store";
import { Modal } from "../components/Modal";
import { observer } from "mobx-react-lite";
import { Button } from "../components/Button";
import './SavingModal.scss';
import Input from "../components/Input";
import ErrorIcon from "../icons/ErrorIcon";
import { publicUrl } from "../utils/url";
import Img from "../components/Img";
import { RenderIf } from "../components/RenderIf";
import { AnimatePresence, motion } from "motion/react";
import { PAGE_DEFAULT, PAGE_SAVE } from "../stores/SaveProject";
import CopyIcon from "../icons/CopyIcon";
import DownloadIcon from "../icons/DownloadIcon.jsx";
import EmailIcon from "../icons/EmailIcon.jsx";
import { Toast } from "../components/Toast";
import Loader from "../components/Loader.jsx";
import { _t } from '../utils/Lang';


const variants = {
    enter: (direction) => ({
        x: direction > 0 ? '100%' : '-100%',
        opacity: 0,
    }),
    center: {
        x: 0,
        opacity: 1,
    },
    exit: (direction) => ({
        x: direction > 0 ? '-100%' : '100%',
        opacity: 0,
    }),
};

const SavingModal = observer(({ mode }) => {
    const [direction, setDirection] = useState(0);
    const [toastOpen, setToastOpen] = useState(false);
    const [pdfToastOpen, setPdfToastOpen] = useState(false);
    const [sendToastOpen, setSendToastOpen] = useState(false);
    const toastTimer = useRef(null);
    const pdfToastTimer = useRef(null);
    const sendToastTimer = useRef(null);
    const saveProject = store.saveProject;
    const isSaved = saveProject.isSaved;

    const goToSave = useCallback(() => {
        setDirection(1);
        saveProject.goToSave();
    }, [saveProject]);

    const goToDefault = useCallback(() => {
        setDirection(-1);
        saveProject.goToDefault();
    }, [saveProject]);

    useEffect(() => {
        return () => {
            clearTimeout(toastTimer.current);
            clearTimeout(pdfToastTimer.current);
            clearTimeout(sendToastTimer.current);
        };
    }, []);

    const handleCopyLink = useCallback(() => {
        saveProject.copyLink();
        setToastOpen(true);
        clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToastOpen(false), 5000);
    }, [saveProject]);

    const handleDownloadPdf = useCallback(async () => {
        await saveProject.generatePdf();
        setPdfToastOpen(true);
        clearTimeout(pdfToastTimer.current);
        pdfToastTimer.current = setTimeout(() => setPdfToastOpen(false), 5000);
    }, [saveProject]);

    const handleSendEmail = useCallback(async () => {
        saveProject.validate();
        if (!saveProject.isValid) return;
        const result = await saveProject.send();
        if (result) {
            setSendToastOpen(true);
            clearTimeout(sendToastTimer.current);
            sendToastTimer.current = setTimeout(() => setSendToastOpen(false), 5000);
        }
    }, [saveProject]);

    return (<>
        <Modal
            mode={mode}
            className="no-padding modal-lg"
            onClose={e => {
                store.closeSavingPopup();
            }}
            isOpen={store.isOpenSavingPopup}
        >
            <div className="SavingModal" style={{ minHeight: 500 }}>
                <div className="row g-0">
                    <div className="col-12 col-md-6 order-md-2 SavingModal__col-content">
                        <div className="modal-padding SavingModal__page-container">
                            <AnimatePresence mode="wait" custom={direction}>
                                {saveProject.page === PAGE_DEFAULT && (
                                    <motion.div
                                        key={PAGE_DEFAULT}
                                        custom={direction}
                                        variants={variants}
                                        initial="enter"
                                        animate="center"
                                        exit="exit"
                                        transition={{ duration: 0.22, ease: 'easeInOut' }}
                                        className="SavingModal__page"
                                    >
                                        <div className='c-h1 mt-3 me-5'>{_t('save_config_later')}</div>
                                        <p>{_t('save_config_desc')}</p>

                                        <div className="SavingModal__photo-preview-mobile d-block d-md-none">
                                            <Img src={store.renderImageUrl} alt="" />
                                        </div>

                                        <div className="mt-4">
                                            <Button onClick={goToSave} className="btn-lg primary w-100">
                                                <EmailIcon className={'me-2'} />
                                                {_t('send_email')}
                                            </Button>
                                            <div className="d-flex g-2">

                                                <Button
                                                    disabled={saveProject.isSavingPdf}
                                                    onClick={handleDownloadPdf}
                                                    className=" btn-link hollow primary w-100 mt-2">
                                                    <DownloadIcon className={'me-2'} />
                                                    {_t('download_pdf')} <Loader visible={saveProject.isSavingPdf} />
                                                </Button>
                                                <Button onClick={handleCopyLink}
                                                    className="btn-link hollow primary w-100 mt-2">
                                                     <CopyIcon className={'me-2'} />
                                                     {_t('copy_link')}
                                                </Button>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                                {saveProject.page === PAGE_SAVE && (
                                    <motion.div
                                        key={PAGE_SAVE}
                                        custom={direction}
                                        variants={variants}
                                        initial="enter"
                                        animate="center"
                                        exit="exit"
                                        transition={{ duration: 0.22, ease: 'easeInOut' }}
                                        className="SavingModal__page"
                                    >
                                        <div className='c-h1 mt-3 me-5'>{isSaved ? _t('send_email') : _t('send_email')}</div>
                                        <p className="fz-16">
                                            {!isSaved ? (
                                                _t('email_desc')
                                            ) : (
                                                _t('email_changes_note')
                                            )}
                                        </p>

                                        <div className="SavingModal__photo-preview-mobile d-block d-md-none">
                                            <Img src={store.renderImageUrl} alt="" />
                                        </div>

                                        <div className="input-wrapper">
                                            <Input
                                                type="email"
                                                label={_t('email_label')}
                                                onChange={e => saveProject.updateField('email', e.target.value)}
                                                onBlur={e => saveProject.validate('email')}
                                                placeholder={_t('email_placeholder')} value={saveProject.email} />

                                            <div className="error" style={{ display: saveProject.hasError('email') ? 'flex' : 'none' }}>
                                                <ErrorIcon />
                                                <span>{_t('email_invalid')}</span>
                                            </div>
                                        </div>

                                        {isSaved ? (
                                            <p>{_t('email_existing')}</p>
                                        ) : (
                                            <p>{_t('email_consent')}</p>
                                        )}

                                        <RenderIf condition={!isSaved}>
                                            <div className="agreements">
                                                <div className="input-wrapper">
                                                    <Checkbox name="terms" checked={saveProject.agree} onChange={e => {
                                                        saveProject.updateField('agree', !saveProject.agree);
                                                        saveProject.validate('agree');
                                                    }}>
                                                         <span className="color-lightgray">{_t('newsletter')}</span>
                                                    </Checkbox>

                                                    <div className="error" style={{ display: saveProject.hasError('agree') ? 'flex' : 'none' }}>
                                                        <ErrorIcon />
                                                         <span>{_t('accept_terms')}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </RenderIf>

                                        <div className="mt-4">
                                            <Button
                                                disabled={saveProject.isValid === false || saveProject.isSaving}
                                                onClick={handleSendEmail}
                                                className="btn-lg primary w-100">
                                                 {isSaved ? _t('save_changes') : _t('save_configuration')} <Loader visible={saveProject.isSaving} />
                                            </Button>
                                            <Button onClick={goToDefault}
                                                className="btn-lg hollow primary w-100 mt-2">
                                                 {_t('back')}
                                            </Button>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>

                    <div className="col-12 col-md-6 d-none d-md-block">
                        <div className="SavingModal__photo-preview">
                            <Img src={store.renderImageUrl} alt="" />
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
        <Toast isOpen={toastOpen}>{_t('toast_link_copied')}</Toast>
        <Toast isOpen={pdfToastOpen}>{_t('toast_pdf_downloaded')}</Toast>
        <Toast isOpen={sendToastOpen}>{_t('toast_email_sent')}</Toast>
    </>)
});

export default SavingModal
